import QueryKeys from '@constants/query-keys';
import { Table } from '@molecules';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import LinkManagementAssetSelect from '@organisms/Enterprise/LinksManagement/LinkManagementAssetSelect/LinkManagementAssetSelect';
import { TableSelectType } from '@services';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { StyledSubmitAndCopyBtn } from './styles';
import { IEnterpriseLinksManagementTableProps, ITableLinksManagement } from './types';
import { FILTERS_LIST, getColumns } from './utils';
import { useQuery } from 'react-query';
import { TCompanyAsset } from '@organisms/Enterprise/LinksManagement/types';
import { ELinkManagementTabs } from '@organisms/Enterprise/LinksManagement/enums';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { ALL_OPTIONS_ASSETS } from './constants';
import { getParams } from '@helpers';
import { useEnterpriseContext } from '@contexts/Enterprise/EnterpriseProvider';

const EnterpriseLinksManagementTable = ({ selectedTab }: IEnterpriseLinksManagementTableProps) => {
  const { id: organizationId } = useParams<{ id: string }>();
  const [companyAssetSelected, setCompanyAssetSelected] = useState<string>(ELinkManagementTabs.All);
  const { search } = useLocation();

  const { organizationDetail } = useEnterpriseContext();

  const { data: listAsset } = useQuery<unknown, unknown, TCompanyAsset[]>(
    `enterprises-admin/${organizationId}/assets`,
    () =>
      ApiClientEnterprise.get(`/enterprises-admin/${organizationId}/assets`).then(
        (res) => res.data,
      ),
  );

  const listAssetData = useMemo(() => {
    return listAsset
      ? [
          {
            ...ALL_OPTIONS_ASSETS,
            photoUrl: organizationDetail?.logo
              ? `${process.env.REACT_APP_CLOUDFRONT_URL}/${organizationDetail.logo}`
              : '',
          },
          ...listAsset,
        ]
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listAsset]);

  const columns = useMemo(() => getColumns(), []);

  const params = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return {
      ...getParams(searchParams),
      status: searchParams.getAll('status'),
      url: searchParams.get('url') || '',
    };
  }, [search]);

  const preSelectedFilters = useMemo(() => {
    return {
      id: organizationId || '',
      identifiedType: selectedTab !== ELinkManagementTabs.All ? selectedTab : undefined,
      assetId: companyAssetSelected !== ELinkManagementTabs.All ? companyAssetSelected : undefined,
      ...params,
    };
  }, [companyAssetSelected, organizationId, params, selectedTab]);

  const getSelectedMultipleValue = useCallback(
    (items: ITableLinksManagement[], selectedItemsId: string[]) => {
      const selectedItems = items.filter((item) =>
        selectedItemsId.includes((item as { id: string })?.id),
      );

      return selectedItems;
    },
    [],
  );

  const handleAssetChange = useCallback((event: SelectChangeEvent<string>) => {
    setCompanyAssetSelected(event.target.value);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Typography variant="h5" mb="20px">
          User related links
        </Typography>
      </Box>
      <Table<ITableLinksManagement>
        queryName={QueryKeys.enterpriseLinksManagement}
        columns={columns}
        searchPlaceholder="Search for links"
        apiRoute={`/enterprises-admin/${organizationId}/links`}
        rowsPerPageOptions={[10, 20, 50, 100, 200]}
        preSelectedFilters={preSelectedFilters}
        checkboxSelection
        showSelectedRows={true}
        hideFooterSelectedRowCount={false}
        getSelectedMultipleValue={getSelectedMultipleValue}
        selectedRowType={TableSelectType.LinkManagement}
        filtersList={FILTERS_LIST}
        isEnterprise
        customFilterSx={{
          order: 2,
        }}
        textSearchParams="url"
        renderAdditionalControl={
          <>
            <LinkManagementAssetSelect
              handleOnChange={handleAssetChange}
              listAsset={listAssetData}
              value={companyAssetSelected}
            />
            <StyledSubmitAndCopyBtn disabled>Submit and Copy</StyledSubmitAndCopyBtn>
          </>
        }
      />
    </Box>
  );
};

export default EnterpriseLinksManagementTable;
